import { differenceInYears } from 'date-fns';
import { StringSchema } from 'yup';
import * as yup from 'yup';

import { Nullable } from '../types';
import { stringToNumber, yupSchemaIsMixed, yupSchemaIsString } from './general';

export type ValidationRequiredWhenFn = (currentValue: unknown) => boolean;

type ValidationRules = {
  date?: boolean;
  monthFullYearDate?: boolean;
  email?: boolean;
  is?: any;
  length?: number;
  maxCharLength?: number;
  min?: number;
  phone?: boolean;
  phoneMobile?: boolean;
  phoneLandline?: boolean;
  required?: boolean;
  lessThanNumericField?: [string, string];
  lessThanOrEqualNumericField?: [string, string];
  minNumericValue?: number;
  maxNumericValue?: number;
  onlyLetters?: boolean;
  onlyNumbers?: boolean;
  minAge?: number;
  onlyCharactersAndPunctuation?: boolean;
  personName?: boolean;
  requiredWhen?: Record<string, ValidationRequiredWhenFn>;
  notEqualTo?: [string, string];
  withinPercentageRangeOfField?: [string, string, string, string];
};

export enum PhoneType {
  Mobile = 'mobile',
  Landline = 'landline',
  Any = 'any',
}

const getPhonePrefixCharacterClassForPhoneType = (
  phoneType: PhoneType,
): string => {
  switch (phoneType) {
    case PhoneType.Mobile:
      return '[4]';
    case PhoneType.Landline:
      return '[2-357-8]';
    case PhoneType.Any:
      return '[2-57-8]';
    default:
      throw new Error(`Unsupported phone type: ${phoneType}`);
  }
};

export const getPhoneValidationRegex = (phoneType: PhoneType) => {
  const areaCode = getPhonePrefixCharacterClassForPhoneType(phoneType);
  return new RegExp(
    `^(?:\\+?(61))? ?(?:\\((?=.*\\)))?(0?${areaCode})\\)? ?(\\d\\d(?:[- ](?=\\d{3})|(?!\\d\\d[- ]?\\d[- ]))\\d\\d[- ]?\\d[- ]?\\d{3})$`,
  );
};

// Person names: Latin letters, spaces, hyphens, apostrophes (straight and
// curly) and full stops. Kept in sync with the backend PersonName rule.
export const PERSON_NAME_REGEX = /^[a-zA-Z .'’-]*$/;
export const PERSON_NAME_VALIDATION_MESSAGE =
  'Only letters, spaces, hyphens, apostrophes and full stops are allowed for this field';

export const buildValidationSchema = (
  inputSchema: StringSchema<Nullable<string | undefined>>,
  rules: ValidationRules,
): StringSchema =>
  Object.keys(rules).reduce((schema, key) => {
    const ruleName = key as keyof ValidationRules;
    const value = rules[ruleName];
    const valueAsNumber = value as number;
    const valueAsTuple = value as [string, string];
    const valueAsRecord = value as Record<string, ValidationRequiredWhenFn>;
    const valueAsComparisonParams = value as [string, string, string, string];

    if (yupSchemaIsString(schema)) {
      switch (ruleName) {
        case 'date': {
          const validationMessage =
            'This field must match the following date format: DD/MM/YYYY';

          return schema.matches(/^\d{4}-\d{2}-\d{2}$/, validationMessage);
        }
        case 'monthFullYearDate': {
          const validationMessage =
            'This field must match the following date format: MM/YYYY';
          return schema.matches(/^\d{2}\/\d{4}$/, validationMessage);
        }

        case 'onlyLetters': {
          return schema.matches(
            /^[aA-zZ\s]*$/,
            'Only alphabets are allowed for this field',
          );
        }

        case 'onlyCharactersAndPunctuation': {
          return schema.matches(
            /^[a-zA-Z,\-'']+$/,
            'Only characters and punctuation are allowed for this field',
          );
        }

        case 'personName': {
          return schema.matches(
            PERSON_NAME_REGEX,
            PERSON_NAME_VALIDATION_MESSAGE,
          );
        }

        case 'onlyNumbers': {
          return schema.matches(/^\d*$/, 'The field should have digits only');
        }

        case 'minAge': {
          return schema.test(
            'DOB',
            `You must be at least ${valueAsNumber} years old`,
            (valueToValidate) =>
              differenceInYears(new Date(), new Date(valueToValidate || '')) >=
              valueAsNumber,
          );
        }

        case 'email':
          return schema.email();

        case 'is':
          return schema.oneOf(
            [value],
            `This field must match the value '${value}'`,
          );

        case 'length':
          return schema.length(valueAsNumber);

        case 'maxCharLength':
          return schema.max(valueAsNumber);

        case 'minNumericValue': {
          return schema.test(
            'minNumericValue',
            `This field must be greater than or equal to ${valueAsNumber}`,
            (valueToValidate) =>
              valueToValidate
                ? stringToNumber(valueToValidate) >= valueAsNumber
                : true,
          );
        }

        case 'maxNumericValue': {
          return schema.test(
            'maxNumericValue',
            `This field must be less than or equal to ${valueAsNumber}`,
            (valueToValidate) =>
              valueToValidate
                ? stringToNumber(valueToValidate as string) <= valueAsNumber
                : true,
          );
        }

        case 'min':
          return schema.min(valueAsNumber);

        case 'phone':
          return schema.matches(
            getPhoneValidationRegex(PhoneType.Any),
            'This field must be a valid phone number',
          );

        case 'phoneMobile':
          return schema.matches(
            getPhoneValidationRegex(PhoneType.Mobile),
            'This field must be a valid mobile number',
          );

        case 'phoneLandline':
          return schema.matches(
            getPhoneValidationRegex(PhoneType.Landline),
            'This field must be a valid landline number',
          );

        case 'lessThanNumericField': {
          return schema.when([valueAsTuple[0]], ([fieldValue], fieldSchema) =>
            fieldSchema.test(
              'lessThanNumericField',
              `This field must be less than ${valueAsTuple[1]}.`,
              (valueToValidate: string | undefined) =>
                valueToValidate
                  ? stringToNumber(valueToValidate) < stringToNumber(fieldValue)
                  : true,
            ),
          );
        }

        case 'withinPercentageRangeOfField': {
          return schema.when(
            [valueAsComparisonParams[0]],
            ([fieldValue], fieldSchema) =>
              fieldSchema.test(
                'withinPercentageRangeOfField',
                `This field must be between ${(stringToNumber(fieldValue) + stringToNumber(valueAsComparisonParams[2])).toFixed(2)}% and ${(stringToNumber(fieldValue) + stringToNumber(valueAsComparisonParams[3])).toFixed(2)}%.`,
                (valueToValidate: string | undefined) => {
                  if (!valueToValidate || !fieldValue) return true; // Skip validation if values are missing

                  const baseValue = stringToNumber(fieldValue);
                  const minOffset = stringToNumber(valueAsComparisonParams[2]); // Minimum offset (e.g., `-2`)
                  const maxOffset = stringToNumber(valueAsComparisonParams[3]); // Maximum offset (e.g., `0`)

                  const numericValue = stringToNumber(valueToValidate);
                  const minValue = parseFloat(
                    (baseValue + minOffset).toFixed(2),
                  );
                  const maxValue = parseFloat(
                    (baseValue + maxOffset).toFixed(2),
                  );

                  return numericValue >= minValue && numericValue <= maxValue;
                },
              ),
          );
        }

        case 'lessThanOrEqualNumericField': {
          return schema.when([valueAsTuple[0]], ([fieldValue], fieldSchema) =>
            fieldSchema.test(
              'lessThanOrEqualNumericField',
              `This field must be less than or equal ${valueAsTuple[1]}.`,
              (valueToValidate: string | undefined) =>
                valueToValidate
                  ? stringToNumber(valueToValidate) <=
                    stringToNumber(fieldValue)
                  : true,
            ),
          );
        }

        case 'required': {
          return schema.trim().required();
        }

        case 'requiredWhen': {
          let clonedSchema = schema.clone();

          Object.entries(valueAsRecord).forEach(([valueKey, isRequiredFn]) => {
            clonedSchema = clonedSchema.when(valueKey, (keyBy, fieldSchema) =>
              isRequiredFn(keyBy) ? fieldSchema.trim().required() : fieldSchema,
            );
          });

          return clonedSchema;
        }

        case 'notEqualTo': {
          return schema.notOneOf(
            [yup.ref(valueAsTuple[0]), null],
            `The value cannot be equal to ${valueAsTuple[1]}`,
          );
        }
        default:
      }
    } else if (yupSchemaIsMixed(schema)) {
      switch (ruleName) {
        case 'required':
          return schema.required();
        default:
      }
    }

    return schema;
  }, inputSchema);
